import { create } from "zustand";
import type { ContentSlidePrimaryCompany } from "@/state/contentSlideStore";

type PresentationOutlineFrameItem = {
  id: number;
  src: string;
};

const DEFAULT_SELECTED_OUTLINE_SLIDE_NUMBERS = [1, 3, 7, 4, 8, 9];
const defaultSelectedOutlineFrames: PresentationOutlineFrameItem[] =
  DEFAULT_SELECTED_OUTLINE_SLIDE_NUMBERS.map((n, idx) => ({
    id: idx + 1,
    src: `assets/slides/${encodeURIComponent(`Slide${n}.PNG`)}`,
  }));

interface PresentationStoreState {
  currentStep: number;
  setCurrentStep: (step: number) => void;
  answersByStep: number[];
  setAnswersByStep: (step: number, answers: number) => void;
  questionsPerStep: number[];
  disabledButton: boolean;
  setDisabledButton: (disabled: boolean) => void;
  messages: Array<{ role: "user" | "assistant"; content: string }>;
  addMessage: (role: "user" | "assistant", content: string) => void;
  clearMessages: () => void;
  step3Selection: string | null;
  setStep3Selection: (selection: string | null) => void;
  questionPhase: "initial" | "complete";
  setQuestionPhase: (phase: "initial" | "complete") => void;
  showCreativeService: boolean;
  setShowCreativeService: (value: boolean) => void;
  primaryCompany: ContentSlidePrimaryCompany | null;
  setPrimaryCompany: (value: ContentSlidePrimaryCompany | null) => void;
  selectedPresentationChartTypeId: string | null;
  setSelectedPresentationChartTypeId: (id: string | null) => void;
  sharePriceType: string;
  setSharePriceType: (value: string) => void;
  selectedOutlineFrames: PresentationOutlineFrameItem[];
  addOutlineFrame: (src: string) => void;
  removeOutlineFrame: (id: number) => void;
  reorderOutlineFrames: (dragId: number, dropId: number) => void;
}

export const usePresentationStore = create<PresentationStoreState>((set) => ({
  currentStep: 0,
  setCurrentStep: (step) => set({ currentStep: step }),
  answersByStep: [0, 0, 0, 0],
  setAnswersByStep: (step, answers) =>
    set((state) => {
      const newAnswers = [...state.answersByStep];
      newAnswers[step] = answers;
      return { answersByStep: newAnswers };
    }),
  questionsPerStep: [1, 1, 1, 1],
  disabledButton: true,
  setDisabledButton: (disabled) => set({ disabledButton: disabled }),
  messages: [],
  addMessage: (role, content) =>
    set((state) => ({
      messages: [...state.messages, { role, content }],
    })),
  clearMessages: () => set({ messages: [] }),
  step3Selection: null,
  setStep3Selection: (selection) => set({ step3Selection: selection }),
  questionPhase: "initial",
  setQuestionPhase: (phase) => set({ questionPhase: phase }),
  showCreativeService: false,
  setShowCreativeService: (value) => set({ showCreativeService: value }),
  primaryCompany: null,
  setPrimaryCompany: (value) => set({ primaryCompany: value }),
  selectedPresentationChartTypeId: "share-price-deep-dive",
  setSelectedPresentationChartTypeId: (id) => set({ selectedPresentationChartTypeId: id }),
  sharePriceType: "sharePricePerformance",
  setSharePriceType: (value) => set({ sharePriceType: value }),

  selectedOutlineFrames: defaultSelectedOutlineFrames,
  addOutlineFrame: (src) =>
    set((state) => {
      const maxId =
        state.selectedOutlineFrames.length > 0
          ? Math.max(...state.selectedOutlineFrames.map((f) => f.id))
          : 0;
      return {
        selectedOutlineFrames: [...state.selectedOutlineFrames, { id: maxId + 1, src }],
      };
    }),
  removeOutlineFrame: (id) =>
    set((state) => ({
      selectedOutlineFrames: state.selectedOutlineFrames.filter((f) => f.id !== id),
    })),
  reorderOutlineFrames: (dragId, dropId) =>
    set((state) => {
      if (dragId === dropId) return state;
      const fromIndex = state.selectedOutlineFrames.findIndex((f) => f.id === dragId);
      const toIndex = state.selectedOutlineFrames.findIndex((f) => f.id === dropId);
      if (fromIndex < 0 || toIndex < 0) return state;

      const next = [...state.selectedOutlineFrames];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return { selectedOutlineFrames: next };
    }),
}));
